import { mainApi } from '../config/apiClient';

/**
 * Backend'in fatura kaydında snapshot olarak yazdığı satıcı bilgisi.
 * Companies veya Branches.Firm* tablosundan SellerResolver tarafından çözülür.
 *
 * Frontend bunu yalnızca:
 *  - Ekran açılışında UI'da göstermek (firma adı / VKN)
 *  - PrintReceiptService'te fiş başlığı için
 * kullanır. Fatura kayıt sırasında DTO'ya gönderilse bile backend
 * kendi başına resolve eder (defense-in-depth).
 *
 * Bkz. backend: `gts_dotnetapi/GTS.Business/Managers/Accounting/SellerResolver.cs`
 */
export interface SellerSnapshot {
  sellerCompanyName: string;
  sellerTaxNumber?: string | null;
  sellerTaxOffice?: string | null;
  sellerAddress?: string | null;
  sellerPhone?: string | null;
  sellerEmail?: string | null;
  sellerWebsite?: string | null;
  sellerMersisNo?: string | null;
  sellerTradeRegistryNo?: string | null;
  /** True ise satıcı bilgisi Branches.Firm* alanlarından geldi (franchise şube). */
  isFranchiseInvoice: boolean;
  /** "Companies" | "BranchFirm" — debug/audit. */
  source: 'Companies' | 'BranchFirm';
  /** BranchFirm kaynağında dolu, Companies kaynağında null. */
  sellerBranchId?: number | null;
}

/**
 * Backend'in beklediği ScreenType değerleri.
 * SellerResolver bu string ile QuickSaleScreens / RestaurantScreens / PackageScreens / PykScreens
 * tablolarından BranchId çeker.
 */
export type ScreenType =
  | 'QuickSale'
  | 'Restaurant'
  | 'Package'
  | 'Pyk'
  | 'CallCenter'
  | 'Manual';

/**
 * Atomik satış ekranlarında (QuickSale / Restaurant / Package / Pyk) ekran açılışından
 * sonra çağrılır. screenId üzerinden backend BranchId çıkarıp Companies vs Branches.Firm*
 * arasında karar verir.
 */
export async function getSellerByScreen(
  screenId: number,
  screenType: ScreenType = 'QuickSale',
): Promise<SellerSnapshot> {
  const { data } = await mainApi.get<SellerSnapshot>('/api/SellerInfo/by-screen', {
    params: { screenId, screenType },
  });
  return data;
}

/**
 * CallCenter ve manuel ekranlar gibi branch'i frontend'de seçen akışlar için.
 * branchId string Firebase ID veya int.ToString() formatında olabilir.
 */
export async function getSellerByBranch(
  branchId: string | number,
): Promise<SellerSnapshot> {
  const { data } = await mainApi.get<SellerSnapshot>('/api/SellerInfo/by-branch', {
    params: { branchId: String(branchId) },
  });
  return data;
}

/**
 * Şube/ekran yoksa ana firma bilgisi. Manuel SalesInvoice/PurchaseInvoice mount'ta kullanır.
 */
export async function getSellerByCompany(): Promise<SellerSnapshot> {
  const { data } = await mainApi.get<SellerSnapshot>('/api/SellerInfo/by-company');
  return data;
}
